// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadReorderPad from "@keller/App/Handlers/ReorderPad/ReorderPad";
import { CustomAppState } from "@keller/App/Store/Reducers";
import ReorderPadDateRange from "@keller/App/Widgets/ReorderPad/ReorderPadDateRange";
import ReorderPadPagination from "@keller/App/Widgets/ReorderPad/ReorderPadPagination";
import ReorderPadProductCount, {
    ReorderPadProductCountStyles,
} from "@keller/App/Widgets/ReorderPad/ReorderPadProductCount";
import ReorderPadProductList from "@keller/App/Widgets/ReorderPad/ReorderPadProductList";
import ReorderPadSearchWithin from "@keller/App/Widgets/ReorderPad/ReorderPadSearchWithin";
import ReorderPadSortSelect from "@keller/App/Widgets/ReorderPad/ReorderPadSortSelect";
import ReorderPadViewSelect from "@keller/App/Widgets/ReorderPad/ReorderPadViewSelect";
import { color, xScreen } from "@keller/XDL/Utilities";
import qs from "qs";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export const REORDER_PRODUCTS_ID = "ReorderPad_Products";
interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    return {
        isLoading: state.xReorderPad.isLoading,
        filterQuery: state.xReorderPad.filterQuery,
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    loadReorderPad,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ReorderPadStyles {
    container?: InjectableCss;
    leftColumnContainer?: InjectableCss;
    rightColumnContainer?: InjectableCss;
    mainSectionStyles?: {
        wrapper?: InjectableCss;
        actionsContainer?: InjectableCss;
    };
    filterText?: TypographyPresentationProps;
    mainSectionModal?: {
        header?: InjectableCss;
        actionContainer?: InjectableCss;
        filterSortContainer?: InjectableCss;
        topFilterContainer?: InjectableCss;
        bottomFilterContainer?: InjectableCss;
        sortFilterButton?: ButtonPresentationProps;
        paginationContainer?: InjectableCss;
    };
    modal?: ModalPresentationProps;
}

export const reorderPadStyles: ReorderPadStyles = {
    container: {
        css: css`
            display: flex;
            gap: 42px;
            ${xScreen("lte", "md")} {
                flex-direction: column;
                align-items: flex-start;
                gap: 24px;
            }
        `,
    },
    leftColumnContainer: {
        css: css`
            min-width: 288px;
            width: 288px;
            ${xScreen("lte", "md")} {
                display: none !important;
            }
        `,
    },
    rightColumnContainer: {
        css: css`
            flex-grow: 1;
            width: 100%;
            display: flex;
            flex-direction: column;
            overflow-x: auto;
            gap: 16px;
        `,
    },
    mainSectionStyles: {
        wrapper: {
            css: css`
                width: 100%;
                ${xScreen("lte", "md")} {
                    display: none !important;
                }
            `,
        },
        actionsContainer: {
            css: css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                flex-wrap: wrap;
                gap: 10px 5px;
                padding: 12px 0;
                border-width: 1px 0;
                border-color: ${color("common.border")};
                border-style: solid;
            `,
        },
    },
    filterText: {
        variant: "h3",
        size: 20,
        weight: "bold",
        css: css`
            letter-spacing: 0;
            line-height: 25px;
            margin-bottom: 20px;
        `,
    },
    mainSectionModal: {
        actionContainer: {
            css: css`
                display: flex;
                flex-direction: column;
                width: 100%;
                gap: 15px;
                padding-bottom: 24px;
                border-bottom: 1px solid ${color("common.border")};
                ${xScreen("gt", "md")} {
                    display: none;
                }
            `,
        },
        header: {
            css: css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
                gap: 20px;
            `,
        },
        sortFilterButton: {
            variant: "secondary",
            sizeVariant: "small",
            css: css`
                flex-shrink: 0;
            `,
        },
        filterSortContainer: {
            css: css`
                flex-direction: column;
            `,
        },
        topFilterContainer: {
            css: css`
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 8px;
                gap: 12px 20px;
                width: 100%;
                border: 1px solid ${color("common.border")};
                background: ${color("common.background")};
                padding: 12px;
                ${xScreen("lte", "xs")} {
                    flex-direction: column;
                }
            `,
        },
        bottomFilterContainer: {
            css: css`
                display: flex;
                justify-content: flex-start;
                flex-direction: column;
                gap: 8px;
            `,
        },
        paginationContainer: {
            css: css`
                width: 100%;

                ${xScreen("gt", "md")} {
                    display: none;
                }
            `,
        },
    },
    modal: {
        cssOverrides: {
            modalBody: css`
                &&&& {
                    max-width: 600px;
                }
            `,
        },
    },
};

const styles = reorderPadStyles;

const ReorderPad: FC<Props> = ({ filterQuery, isLoading, location, loadReorderPad }) => {
    const history = useHistory();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [firstLoad, setFirstLoad] = useState(false);

    const loadProducts = (queryString?: string) => {
        const parsedQuery = queryString
            ? qs.parse(queryString.startsWith("?") ? queryString.substr(1) : queryString)
            : undefined;
        const params = parsedQuery ?? {};

        loadReorderPad({
            id: REORDER_PRODUCTS_ID,
            ...params,
            onSuccess: result => {
                if (!firstLoad) {
                    setFirstLoad(true);
                }
            },
        });
    };

    useEffect(() => {
        loadProducts(filterQuery || location.search);
    }, []);

    useEffect(() => {
        if (isLoading || !firstLoad) {
            return;
        }
        const { pathname, search } = location;
        const currentUrl = `${pathname}${search}`;
        const newUrl = filterQuery ? `${pathname}?${filterQuery}` : pathname;
        if (newUrl !== currentUrl) {
            history.push(newUrl);
            loadProducts(filterQuery);
        }
    }, [filterQuery]);

    const modalOpenHandler = () => {
        setModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.mainSectionModal?.actionContainer}>
                <StyledWrapper {...styles.mainSectionModal?.header}>
                    <ReorderPadProductCount />
                    <Button onClick={modalOpenHandler} {...styles.mainSectionModal?.sortFilterButton}>
                        {translate("Sort & Filter")}
                    </Button>
                </StyledWrapper>
                <ReorderPadSearchWithin showTagsOnly={true} />
            </StyledWrapper>
            <StyledWrapper {...styles.leftColumnContainer}>
                <Typography {...styles.filterText} data-test-selector="productListFilterList">
                    {translate("Refine Results")}
                </Typography>
                <ReorderPadSearchWithin />
                <ReorderPadDateRange />
            </StyledWrapper>
            <StyledWrapper data-test-selector="reorderPadRightColumnContainer" {...styles.rightColumnContainer}>
                <StyledWrapper {...styles.mainSectionStyles?.wrapper}>
                    <ReorderPadProductCount />
                    <StyledWrapper {...styles.mainSectionStyles?.actionsContainer}>
                        <ReorderPadSortSelect />
                        <ReorderPadViewSelect />
                        <ReorderPadPagination showResultsPerPage={true} />
                        <ReorderPadPagination showPagesNumber={true} />
                    </StyledWrapper>
                </StyledWrapper>
                <ReorderPadProductList />
            </StyledWrapper>
            <StyledWrapper {...styles.mainSectionModal?.paginationContainer}>
                <ReorderPadPagination showPagesNumber={true} />
            </StyledWrapper>
            <Modal
                headline={translate("Sort & Filter")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
                {...styles.modal}
            >
                <StyledWrapper {...styles.mainSectionModal?.filterSortContainer}>
                    <StyledWrapper {...styles.mainSectionModal?.topFilterContainer}>
                        <ReorderPadSortSelect />
                        <ReorderPadPagination showResultsPerPage={true} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.mainSectionModal?.bottomFilterContainer}>
                        <ReorderPadSearchWithin />
                        <ReorderPadDateRange />
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ReorderPad),
    definition: {
        group: "ReorderPad" as any,
    },
};

export default widgetModule;
